/**
 * @function AccountForm
 * @description Component for creating an account.
 * @returns {JSX.Element | null} - The AccountForm component.
 */
import { CheckCircleIcon } from '@heroicons/react/20/solid'
import { RefObject } from 'react'


/**
 * @interface AccountFormProps
 * @description Props for the AccountForm component.
 * @property {RefObject<HTMLFormElement>} formRef - The reference to the form element.
 * @property {(e: React.FormEvent<HTMLFormElement>) => void} onSubmit - The function to call when the form is submitted.
 * @property {string} password - The password.
 * @property {(value: string) => void} setPassword - The function to set the password.
 * @property {string} repeatPassword - The repeated password.
 * @property {(value: string) => void} setRepeatPassword - The function to set the repeated password.
 * @property {{ test: (password: string) => boolean; description: string }[]} passwordRequirements - The requirements for the password.
 * @property {boolean} passwordValid - Whether the password is valid.
 * @property {boolean} isPending - Whether the password is pending.
 */
interface AccountFormProps {
    formRef: RefObject<HTMLFormElement>
    onSubmit: (e: React.FormEvent<HTMLFormElement>) => void
    password: string
    setPassword: (value: string) => void
    repeatPassword: string
    setRepeatPassword: (value: string) => void
    passwordRequirements: { test: (password: string) => boolean; description: string }[]
    passwordValid: boolean
    isPending: boolean
}

/**
 * @function AccountForm
 * @description Component for creating an account.
 * @returns {JSX.Element | null} - The AccountForm component.
 */
export function AccountForm({
    formRef,
    onSubmit,
    password,
    setPassword,
    repeatPassword,
    setRepeatPassword,
    passwordRequirements,
    passwordValid,
    isPending,
}: AccountFormProps) {
    return (
        <form className="relative mt-8 space-y-6" action="#" ref={formRef} onSubmit={onSubmit}>
            <div className="flex flex-col gap-y-4">
                <div>
                    <label htmlFor="name" className="block text-sm font-medium text-gray-700">
                        Your full name
                    </label>
                    <input
                        id="name"
                        name="name"
                        type="text"
                        autoComplete="name"
                        required
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                </div>
                <div>
                    <label htmlFor="password" className="block text-sm font-medium text-gray-700">
                        Create password
                    </label>
                    <input
                        id="password"
                        name="password"
                        type="password"
                        autoComplete="new-password"
                        required
                        value={password}
                        onChange={(e) => setPassword(e.target.value)}
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                    <ul className="flex flex-col gap-y-1 mt-2 mb-2 text-sm text-gray-600">
                        {passwordRequirements.map((req, index) => (
                            <li
                                key={index}
                                className={`flex items-center ${
                                    req.test(password) ? 'line-through text-gray-500' : ''
                                }`}
                            >
                                {req.test(password) ? (
                                    <CheckCircleIcon
                                        className="h-5 w-5 -ml-0.5 mr-0.5 text-indigo-500"
                                        aria-hidden="true"
                                    />
                                ) : (
                                    <div
                                        className="h-4 w-4 mr-1 border rounded-full border-gray-300 text-white"
                                        aria-hidden="true"
                                    />
                                )}
                                {req.description}
                            </li>
                        ))}
                    </ul>
                </div>

                <div>
                    <label
                        htmlFor="repeatPassword"
                        className="block text-sm font-medium text-gray-700"
                    >
                        Repeat password
                    </label>
                    <input
                        id="repeatPassword"
                        name="repeatPassword"
                        type="password"
                        autoComplete="new-password"
                        required
                        value={repeatPassword}
                        onChange={(e) => setRepeatPassword(e.target.value)}
                        className="mt-1 block w-full py-2 px-3 border border-gray-300 bg-white rounded-md shadow-sm focus:outline-none focus:ring-indigo-500 focus:border-indigo-500 sm:text-sm"
                    />
                </div>
            </div>

            <div>
                <button
                    type="submit"
                    className={`group relative flex w-full justify-center rounded-md border border-transparent bg-indigo-600 py-2 px-4 text-base font-medium text-white disabled:opacity-75 hover:bg-indigo-700 focus:outline-none focus:ring-2 focus:ring-indigo-500 focus:ring-offset-2 ${
                        passwordValid && !isPending ? 'visible' : 'invisible'
                    }`}
                >
                    Continue
                </button>
            </div>
        </form>
    )
}
